import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Users, UserCheck, UserMinus, TrendingDown, TrendingUp, Activity,
  Award, AlertTriangle, RefreshCw, Building2, Briefcase, Clock,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";
import { PageHero, KpiCard, Section, GridSkeleton } from "@/components/AnalyticsCommon";

export default function PerformanceDashboardPage() {
  const { companyParam, companies, selected, select } = useCompany();
  const [filters, setFilters] = useState({ sector_id: "", cargo: "", turno: "", status: "" });
  const [filtersData, setFiltersData] = useState({ sectors: [], cargos: [], turnos: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const params = {};
    if (companyParam) params.company_id = companyParam;
    api.get("/performance/filtros", { params }).then(({ data: d }) => setFiltersData(d));
    setFilters((f) => ({ ...f, sector_id: "", cargo: "", turno: "", status: "" }));
  }, [companyParam]);

  const load = async () => {
    setRefreshing(data !== null);
    if (!data) setLoading(true);
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data: d } = await api.get("/performance/dashboard", { params });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); setRefreshing(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [companyParam, filters]);

  const k = data?.kpis;

  return (
    <div className="space-y-5 pb-10" data-testid="perf-dashboard-page">
      <PageHero
        kicker={<><Activity className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Performance & Analytics</>}
        title="Dashboard Executivo de RH"
        description="Indicadores estratégicos consolidados — turnover, absenteísmo, headcount, performance — com filtros granulares."
        actions={
          <>
            <Select value={selected || "all"} onValueChange={select}>
              <SelectTrigger className="h-10 w-full sm:w-[220px] border-white/20 bg-white/10 text-white" data-testid="perf-company">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas (Grupo)</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={load} disabled={refreshing}
                    className="h-10 border-white/20 bg-white/5 text-white hover:bg-white/15"
                    data-testid="perf-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
          </>
        }
      />

      {/* Filters */}
      <div className="rounded-xl border border-border bg-white p-3 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="perf-filters">
        <FilterSelect label="Setor" value={filters.sector_id} onChange={(v) => setFilters({ ...filters, sector_id: v })}
                      options={filtersData.sectors.map((s) => ({ v: s.id, l: s.name }))} testId="filter-sector" />
        <FilterSelect label="Cargo" value={filters.cargo} onChange={(v) => setFilters({ ...filters, cargo: v })}
                      options={filtersData.cargos.map((c) => ({ v: c, l: c }))} testId="filter-cargo" />
        <FilterSelect label="Turno" value={filters.turno} onChange={(v) => setFilters({ ...filters, turno: v })}
                      options={filtersData.turnos.map((t) => ({ v: t, l: t }))} testId="filter-turno" />
        <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })}
                      options={[
                        { v: "ativo", l: "Ativo" }, { v: "afastado", l: "Afastado" },
                        { v: "ferias", l: "Férias" }, { v: "em_experiencia", l: "Em experiência" },
                      ]} testId="filter-status" />
      </div>

      {/* Hero KPIs */}
      {loading || !k ? <GridSkeleton rows={4} /> : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="perf-kpis-hero">
          <KpiCard testId="kpi-headcount" label="Headcount atual" value={k.headcount}
                  sub={`${k.admitidos_mes} admitidos no mês`} icon={Users} accent="emerald" />
          <KpiCard testId="kpi-turnover-month" label="Turnover mensal" value={`${k.turnover_monthly_pct}%`}
                  sub={`${k.desligados_mes} desligamentos no mês`} icon={TrendingDown}
                  accent={k.turnover_monthly_pct < 1 ? "emerald" : k.turnover_monthly_pct < 3 ? "ochre" : "rose"} />
          <KpiCard testId="kpi-turnover-year" label="Turnover 12 meses" value={`${k.turnover_annual_pct}%`}
                  sub="Acumulado anual" icon={TrendingDown}
                  accent={k.turnover_annual_pct < 8 ? "emerald" : k.turnover_annual_pct < 15 ? "ochre" : "rose"} />
          <KpiCard testId="kpi-absent" label="Absenteísmo mensal"
                  value={k.absenteismo_data_status === "vazio" ? "—" : `${k.absenteismo_monthly_pct}%`}
                  sub={k.absenteismo_data_status === "vazio" ? "Sem dados cadastrados" : "Mês corrente"}
                  icon={Clock} accent={k.absenteismo_data_status === "vazio" ? "slate" : "ochre"} />
        </div>
      )}

      {!loading && k && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="perf-kpis-secondary">
          <KpiCard testId="kpi-admitted" label="Admitidos no mês" value={k.admitidos_mes} icon={UserCheck} accent="sky" />
          <KpiCard testId="kpi-terminated" label="Desligados no mês" value={k.desligados_mes} icon={UserMinus} accent="rose" />
          <KpiCard testId="kpi-perf-avg" label="Média desempenho"
                  value={k.media_desempenho ?? "—"} sub="Escala 1-5" icon={Award} accent="ochre" />
          <KpiCard testId="kpi-appr-pct" label="Avaliações concluídas" value={`${k.avaliacoes_pct_concluidas}%`}
                  sub={`${k.avaliacoes_concluidas}/${k.avaliacoes_total}`} icon={Award} accent="emerald" />
        </div>
      )}

      {/* Charts */}
      {!loading && data && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section kicker="Movimentação" title="Admissões × Desligamentos" description="Últimos 12 meses" testId="chart-movement">
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.timeseries} margin={{ top: 5, right: 8, bottom: 5, left: -8 }}>
                    <defs>
                      <linearGradient id="adm-g" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3a7559" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#3a7559" stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="des-g" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C5A059" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#C5A059" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} width={28} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #E5E7EB" }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="admissoes" stroke="#3a7559" strokeWidth={2} fill="url(#adm-g)" name="Admissões" />
                    <Area type="monotone" dataKey="desligamentos" stroke="#C5A059" strokeWidth={2} fill="url(#des-g)" name="Desligamentos" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Section>

            <Section kicker="Tendência" title="Evolução do turnover" description="% mensal · 12 meses" testId="chart-turnover-trend">
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.timeseries} margin={{ top: 5, right: 8, bottom: 5, left: -8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} width={32} unit="%" />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Line type="monotone" dataKey="turnover_pct" stroke="#234935" strokeWidth={2.5} dot={{ r: 3 }} name="Turnover %" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Section>
          </div>

          <Section kicker="Quadro" title="Headcount × Turnover por setor" description="Top 15 setores" testId="chart-headcount-sector">
            <div className="h-[360px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.by_sector.slice(0, 15)} margin={{ top: 5, right: 10, bottom: 60, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" />
                  <XAxis dataKey="sector" tick={{ fontSize: 10, fill: "#374151" }} angle={-30} textAnchor="end" height={70} interval={0} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} unit="%" />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="l" dataKey="headcount" fill="#234935" name="Headcount" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="r" dataKey="turnover_pct" fill="#C5A059" name="Turnover %" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <Section kicker="Setores críticos" title="Maior turnover (12 meses)" testId="critical-sectors">
                <div className="overflow-x-auto">
                  <table className="w-full text-[12.5px]">
                    <thead>
                      <tr className="bg-slate-50 border-b border-border">
                        <th className="text-left px-3 py-2">#</th>
                        <th className="text-left px-3 py-2">Setor</th>
                        <th className="text-right px-3 py-2">Headcount</th>
                        <th className="text-right px-3 py-2">Desl. 12m</th>
                        <th className="text-right px-3 py-2">Turnover</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.by_sector].sort((a, b) => b.turnover_pct - a.turnover_pct).slice(0, 8).map((s, i) => (
                        <tr key={s.sector_id} className="border-b border-border last:border-0">
                          <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                          <td className="px-3 py-2 font-medium">{s.sector}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{s.headcount}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{s.desligamentos_12m}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold">{s.turnover_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            </div>
            <Section kicker="Alertas" title="Insights" testId="insights">
              <div className="space-y-3">
                {k.setor_maior_turnover && k.setor_maior_turnover.turnover_pct > 0 && (
                  <Insight icon={AlertTriangle} tone="rose"
                           title="Setor com maior turnover"
                           value={k.setor_maior_turnover.sector}
                           sub={`${k.setor_maior_turnover.turnover_pct}% nos últimos 12 meses`} />
                )}
                {k.media_desempenho !== null && (
                  <Insight icon={Award} tone={k.media_desempenho >= 4 ? "emerald" : "ochre"}
                           title="Performance global"
                           value={`${k.media_desempenho}/5`}
                           sub={`Baseado em ${k.avaliacoes_concluidas} avaliações concluídas`} />
                )}
                {k.absenteismo_data_status === "vazio" && (
                  <Insight icon={Clock} tone="slate"
                           title="Absenteísmo"
                           value="Aguardando dados"
                           sub="Cadastre lançamentos para ativar análises" />
                )}
                {k.admitidos_mes > k.desligados_mes && (
                  <Insight icon={TrendingUp} tone="emerald"
                           title="Crescimento líquido"
                           value={`+${k.admitidos_mes - k.desligados_mes}`}
                           sub="Saldo positivo de admissões no mês" />
                )}
              </div>
            </Section>
          </div>
        </>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <Label className="text-[10.5px] uppercase tracking-wider">{label}</Label>
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger className="h-9" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos</SelectItem>
          {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Insight({ icon: Icon, tone, title, value, sub }) {
  const cls = {
    rose:    "border-rose-200 bg-rose-50/40 text-rose-700",
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-700",
    ochre:   "border-[#C5A059]/30 bg-[#FAF4E5]/40 text-[#8A6B2B]",
    slate:   "border-slate-200 bg-slate-50/40 text-slate-600",
  }[tone] || "border-border bg-slate-50/40 text-slate-700";
  return (
    <div className={`rounded-md border ${cls} p-3 flex items-start gap-2.5`}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider opacity-70 font-medium">{title}</div>
        <div className="font-display text-[15px] font-semibold leading-tight mt-0.5 truncate">{value}</div>
        <div className="text-[11px] opacity-80 mt-0.5">{sub}</div>
      </div>
    </div>
  );
}
